/**
 * PresenterOverlay.tsx
 * Full-screen overlay rendered during Slide Show mode.
 * Contains the floating toolbar and all presenter tools.
 */

import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { X } from 'lucide-react';
import { useAppStore } from '@store/appStore';
import { usePresenterStore } from '@store/presenterStore';
import FloatingToolbar from './Toolbar/FloatingToolbar';
import PenTool from './Toolbar/PenTool';
import WhiteboardManager from './Whiteboard/WhiteboardManager';
import EmbeddedBrowser from './Browser/EmbeddedBrowser';
import RandomNamePicker from './NamePicker/RandomNamePicker';

export default function PresenterOverlay(): React.ReactElement {
  const setPresenterOverlayVisible = useAppStore((s) => s.setPresenterOverlayVisible);
  const { activeTool, whiteboardOpen, browserOpen, namePickerOpen } = usePresenterStore();
  const canvasRef = useRef<HTMLCanvasElement>(null);

  // Prevent scroll behind overlay
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  return (
    <motion.div
      className="fixed inset-0 z-[9000] pointer-events-none"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      {/* Drawing canvas — transparent overlay, pointer events when tool active */}
      {(activeTool === 'pen' || activeTool === 'highlighter' || activeTool === 'laser') && (
        <PenTool canvasRef={canvasRef} />
      )}

      {/* Laser dot tracker */}
      {activeTool === 'laser' && <LaserDot />}

      {/* Whiteboard panel */}
      {whiteboardOpen && <WhiteboardManager />}

      {/* Embedded browser */}
      {browserOpen && <EmbeddedBrowser />}

      {/* Name picker */}
      {namePickerOpen && <RandomNamePicker />}

      {/* Floating toolbar — always visible, always interactive */}
      <div className="pointer-events-auto">
        <FloatingToolbar />
      </div>

      {/* Exit overlay button */}
      <motion.button
        className="pointer-events-auto fixed top-4 right-4 z-[9100] flex items-center gap-1.5 px-3 py-2 glass rounded-xl text-xs font-semibold text-slate-700 hover:text-slate-900 transition-colors"
        onClick={() => setPresenterOverlayVisible(false)}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
      >
        <X size={13} />
        Exit Presenter Mode
      </motion.button>
    </motion.div>
  );
}

/** Small laser dot that follows mouse position */
function LaserDot(): React.ReactElement {
  const { laserPosition, setLaserPosition } = usePresenterStore();

  useEffect(() => {
    const handler = (e: MouseEvent) => setLaserPosition({ x: e.clientX, y: e.clientY });
    window.addEventListener('mousemove', handler);
    return () => window.removeEventListener('mousemove', handler);
  }, [setLaserPosition]);

  if (!laserPosition) return <></>;

  return (
    <div
      className="laser-dot"
      style={{ left: laserPosition.x, top: laserPosition.y }}
    />
  );
}
